import React, { useState, useEffect } from 'react';
import { Clock, Save, X, ArrowLeft, Plus, Trash2 } from 'lucide-react';
import timetableService from '../../services/timetableService';
import '../../styles/TimetableSettings.css';

const TimetableSettings = () => {
  const [timetableSetup, setTimetableSetup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('view'); // view, modify
  const [formData, setFormData] = useState({
    school_year_start: '1998-09-02',
    school_year_end: '1999-08-31',
    winter_time_start: '1998-09-02',
    winter_time_end: '1999-08-31',
    summer_time_start: '1998-09-02',
    summer_time_end: '1999-08-31'
  });
  
  // Normal Day periods and breaks
  const [normalPeriods, setNormalPeriods] = useState([
    { season: 'Winter', period: 'P00', start: '08:00', end: '08:45', description: 'Assembly' },
    { season: 'Winter', period: 'P01', start: '08:45', end: '09:26', description: 'Period 1' },
    { season: 'Winter', period: 'P02', start: '09:25', end: '10:05', description: 'Period 2' },
    { season: 'Winter', period: 'P03', start: '10:05', end: '10:45', description: 'Period 3' },
    { season: 'Winter', period: 'P04', start: '11:00', end: '11:40', description: 'Period 4' },
    { season: 'Winter', period: 'P05', start: '11:40', end: '12:20', description: 'Period 5' },
    { season: 'Winter', period: 'P06', start: '13:20', end: '14:00', description: 'Period 6' },
    { season: 'Winter', period: 'P07', start: '14:00', end: '14:40', description: 'Period 7' },
    { season: 'Winter', period: 'P08', start: '14:40', end: '15:20', description: 'Period 8' }
  ]);
  
  const [normalBreaks, setNormalBreaks] = useState([
    { period: 'P01', description: 'MornAsmb' },
    { period: 'P02', description: '' },
    { period: 'P03', description: '' },
    { period: 'P04', description: 'Recess' },
    { period: 'P05', description: '' },
    { period: 'P06', description: 'Lunch' },
    { period: 'P07', description: '' },
    { period: 'P08', description: '' }
  ]);
  
  // Special Day periods and breaks
  const [specialPeriods, setSpecialPeriods] = useState([]);
  const [specialBreaks, setSpecialBreaks] = useState([]);
  
  // Form inputs for adding new periods/breaks
  const [newPeriod, setNewPeriod] = useState({
    season: 'Winter',
    period: '',
    start: '',
    end: '',
    description: ''
  });
  
  const [newBreak, setNewBreak] = useState({
    period: '',
    description: ''
  });
  
  const [breakBeforePeriods, setBreakBeforePeriods] = useState(3);

  useEffect(() => {
    loadTimetableSetup();
  }, []);

  const loadTimetableSetup = async () => {
    try {
      const data = await timetableService.getTimetableSetup();
      setTimetableSetup(data);
      if (data) {
        setFormData({
          school_year_start: data.school_year_start_date || '1998-09-02',
          school_year_end: data.school_year_end_date || '1999-08-31',
          winter_time_start: data.winter_time_start_date || '1998-09-02',
          winter_time_end: data.winter_time_end_date || '1999-08-31',
          summer_time_start: data.summer_time_start_date || '1998-09-02',
          summer_time_end: data.summer_time_end_date || '1999-08-31'
        });
      }
    } catch (error) {
      console.error('Error loading timetable setup:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleModify = () => {
    setMode('modify');
  };

  const handleCancel = () => {
    setMode('view');
    loadTimetableSetup();
  };

  const handleSave = async () => {
    try {
      const updates = {
        school_year_start_date: formData.school_year_start,
        school_year_end_date: formData.school_year_end,
        winter_time_start_date: formData.winter_time_start,
        winter_time_end_date: formData.winter_time_end,
        summer_time_start_date: formData.summer_time_start,
        summer_time_end_date: formData.summer_time_end
      };
      
      await timetableService.updateTimetableSetup(updates);
      setMode('view');
      await loadTimetableSetup();
    } catch (error) {
      console.error('Error saving timetable setup:', error);
    }
  };

  const addPeriod = (isSpecial = false) => {
    if (isSpecial) {
      setSpecialPeriods(prev => [...prev, { ...newPeriod, season: 'Summer' }]);
    } else {
      setNormalPeriods(prev => [...prev, { ...newPeriod }]);
    }
    setNewPeriod({ season: 'Winter', period: '', start: '', end: '', description: '' });
  };

  const addBreak = (isSpecial = false) => {
    if (isSpecial) {
      setSpecialBreaks(prev => [...prev, { ...newBreak }]);
    } else {
      setNormalBreaks(prev => [...prev, { ...newBreak }]);
    }
    setNewBreak({ period: '', description: '' });
  };

  const removePeriod = (index, isSpecial = false) => {
    if (isSpecial) {
      setSpecialPeriods(prev => prev.filter((_, i) => i !== index));
    } else {
      setNormalPeriods(prev => prev.filter((_, i) => i !== index));
    }
  };

  const removeBreak = (index, isSpecial = false) => {
    if (isSpecial) {
      setSpecialBreaks(prev => prev.filter((_, i) => i !== index));
    } else {
      setNormalBreaks(prev => prev.filter((_, i) => i !== index));
    }
  };

  if (loading) {
    return (
      <div className="timetable-settings-container">
        <div className="loading-container">
          <div className="loading-content">
            <div className="loading-spinner">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
            <p className="loading-text">載入時間表設定中...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="timetable-settings-container">
      {/* Header */}
      <div className="timetable-settings-header">
        <h2>School Timetable Setup (TBM0115E)</h2>
        <p className="subtitle">時間表設定 - 學年時間和課節安排</p>
      </div>

      {/* Global Settings */}
      <div className="global-settings-panel">
        <div className="global-settings-content">
          <div className="date-format-label">(DD/MM/YYYY)</div>
          
          <div className="date-inputs-grid">
            {/* School Year */}
            <div className="date-input-group">
              <label className="date-input-label">School Year:</label>
              <div className="date-inputs-row">
                <input
                  type="date"
                  value={formData.school_year_start}
                  onChange={(e) => handleInputChange('school_year_start', e.target.value)}
                  disabled={mode === 'view'}
                  className="date-input"
                />
                <span className="date-separator">-</span>
                <input
                  type="date"
                  value={formData.school_year_end}
                  onChange={(e) => handleInputChange('school_year_end', e.target.value)}
                  disabled={mode === 'view'}
                  className="date-input"
                />
              </div>
            </div>
            
            {/* Winter Time */}
            <div className="date-input-group">
              <label className="date-input-label">Winter Time:</label>
              <div className="date-inputs-row">
                <input
                  type="date"
                  value={formData.winter_time_start}
                  onChange={(e) => handleInputChange('winter_time_start', e.target.value)}
                  disabled={mode === 'view'}
                  className="date-input"
                />
                <span className="date-separator">-</span>
                <input
                  type="date"
                  value={formData.winter_time_end}
                  onChange={(e) => handleInputChange('winter_time_end', e.target.value)}
                  disabled={mode === 'view'}
                  className="date-input"
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="action-buttons-panel">
          <button
            onClick={handleModify}
            disabled={mode === 'modify'}
            className="action-button primary"
          >
            <Clock size={16} />
            Modify
          </button>
          <button
            onClick={() => window.history.back()}
            className="action-button secondary"
          >
            <ArrowLeft size={16} />
            Prev Page
          </button>
          <button
            onClick={handleCancel}
            disabled={mode === 'view'}
            className="action-button danger"
          >
            <X size={16} />
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={mode === 'view'}
            className="action-button success"
          >
            <Save size={16} />
            Save
          </button>
        </div>
      </div>

      {/* Normal Day and Special Day Sections */}
      <div className="day-panels-container">
        {/* Normal Day Section */}
        <div className="day-panel">
          <div className="day-panel-header">
            <h3 className="day-panel-title">Normal Day</h3>
          </div>
          
          <div className="day-panel-content">
            {/* Period Definition */}
            <div className="period-definition-section">
              <h4 className="period-definition-title">Period Definition</h4>
              <div className="period-inputs-grid">
                <div className="period-input-group">
                  <label className="period-input-label">Season:</label>
                  <input
                    type="text"
                    value={newPeriod.season}
                    onChange={(e) => setNewPeriod(prev => ({ ...prev, season: e.target.value }))}
                    disabled={mode === 'view'}
                    className="period-input"
                  />
                </div>
                <div className="period-input-group">
                  <label className="period-input-label">Period:</label>
                  <input
                    type="text"
                    value={newPeriod.period}
                    onChange={(e) => setNewPeriod(prev => ({ ...prev, period: e.target.value }))}
                    disabled={mode === 'view'}
                    className="period-input"
                    placeholder="P01"
                  />
                </div>
                <div className="period-input-group">
                  <label className="period-input-label">Start:</label>
                  <input
                    type="time"
                    value={newPeriod.start}
                    onChange={(e) => setNewPeriod(prev => ({ ...prev, start: e.target.value }))}
                    disabled={mode === 'view'}
                    className="period-input"
                  />
                </div>
                <div className="period-input-group">
                  <label className="period-input-label">End:</label>
                  <input
                    type="time"
                    value={newPeriod.end}
                    onChange={(e) => setNewPeriod(prev => ({ ...prev, end: e.target.value }))}
                    disabled={mode === 'view'}
                    className="period-input"
                  />
                </div>
              </div>
              <button
                onClick={() => addPeriod(false)}
                disabled={mode === 'view' || !newPeriod.period || !newPeriod.start || !newPeriod.end}
                className="update-button"
              >
                <Plus size={16} />
                Update
              </button>
            </div>

            {/* Period List */}
            <div className="period-list-section">
              <h4 className="period-list-title">Period List</h4>
              <div className="period-list">
                {normalPeriods.map((period, index) => (
                  <div key={index} className="period-item">
                    <span className="period-item-text">
                      {period.season}-{period.period}-{period.start}-{period.end}...
                    </span>
                    {mode === 'modify' && (
                      <button
                        onClick={() => removePeriod(index, false)}
                        className="remove-period-btn"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Break Definition */}
            <div className="break-definition-section">
              <h4 className="break-definition-title">Break Definition</h4>
              <div className="break-inputs-grid">
                <div className="break-input-group">
                  <label className="break-input-label">Period:</label>
                  <div className="break-input-with-dropdown">
                    <input
                      type="text"
                      value={newBreak.period}
                      onChange={(e) => setNewBreak(prev => ({ ...prev, period: e.target.value }))}
                      disabled={mode === 'view'}
                      className="break-input"
                      placeholder="P01"
                    />
                    <button className="break-dropdown-btn">
                      ▼
                    </button>
                  </div>
                </div>
                <div className="break-input-group">
                  <label className="break-input-label">Break Description:</label>
                  <input
                    type="text"
                    value={newBreak.description}
                    onChange={(e) => setNewBreak(prev => ({ ...prev, description: e.target.value }))}
                    disabled={mode === 'view'}
                    className="break-input"
                    placeholder="MornAsmb"
                  />
                </div>
                <div className="break-input-group">
                  <button
                    onClick={() => addBreak(false)}
                    disabled={mode === 'view' || !newBreak.period}
                    className="update-button"
                  >
                    Update
                  </button>
                </div>
              </div>
            </div>

            {/* Break Before Periods */}
            <div className="break-before-section">
              <label className="break-before-label">Break Before Periods:</label>
              <input
                type="number"
                value={breakBeforePeriods}
                onChange={(e) => setBreakBeforePeriods(parseInt(e.target.value) || 0)}
                disabled={mode === 'view'}
                className="break-before-input"
              />
            </div>

            {/* Break List */}
            <div className="break-list-section">
              <h4 className="break-list-title">Break List</h4>
              <div className="break-list">
                {normalBreaks.map((breakItem, index) => (
                  <div key={index} className={`break-item ${index === 0 ? 'selected' : ''}`}>
                    <span className="break-item-text">
                      {breakItem.period}-{breakItem.description || 'Empty'}
                    </span>
                    {mode === 'modify' && (
                      <button
                        onClick={() => removeBreak(index, false)}
                        className="remove-break-btn"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Special Day Section */}
        <div className="day-panel">
          <div className="day-panel-header">
            <h3 className="day-panel-title">Special Day</h3>
          </div>
          
          <div className="day-panel-content">
            {/* Period Definition */}
            <div className="period-definition-section">
              <h4 className="period-definition-title">Period Definition</h4>
              <div className="period-inputs-grid">
                <div className="period-input-group">
                  <label className="period-input-label">Season:</label>
                  <input
                    type="text"
                    value="Summer"
                    disabled
                    className="period-input"
                  />
                </div>
                <div className="period-input-group">
                  <label className="period-input-label">Period:</label>
                  <input
                    type="text"
                    value={newPeriod.period}
                    onChange={(e) => setNewPeriod(prev => ({ ...prev, period: e.target.value }))}
                    disabled={mode === 'view'}
                    className="period-input"
                    placeholder="P01"
                  />
                </div>
                <div className="period-input-group">
                  <label className="period-input-label">Start:</label>
                  <input
                    type="time"
                    value={newPeriod.start}
                    onChange={(e) => setNewPeriod(prev => ({ ...prev, start: e.target.value }))}
                    disabled={mode === 'view'}
                    className="period-input"
                  />
                </div>
                <div className="period-input-group">
                  <label className="period-input-label">End:</label>
                  <input
                    type="time"
                    value={newPeriod.end}
                    onChange={(e) => setNewPeriod(prev => ({ ...prev, end: e.target.value }))}
                    disabled={mode === 'view'}
                    className="period-input"
                  />
                </div>
              </div>
              <button
                onClick={() => addPeriod(true)}
                disabled={mode === 'view' || !newPeriod.period || !newPeriod.start || !newPeriod.end}
                className="update-button"
              >
                <Plus size={16} />
                Update
              </button>
            </div>

            {/* Period List */}
            <div className="period-list-section">
              <h4 className="period-list-title">Period List</h4>
              <div className="period-list">
                {specialPeriods.map((period, index) => (
                  <div key={index} className="period-item">
                    <span className="period-item-text">
                      {period.season}-{period.period}-{period.start}-{period.end}...
                    </span>
                    {mode === 'modify' && (
                      <button
                        onClick={() => removePeriod(index, true)}
                        className="remove-period-btn"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Break Definition */}
            <div className="break-definition-section">
              <h4 className="break-definition-title">Break Definition</h4>
              <div className="break-inputs-grid">
                <div className="break-input-group">
                  <label className="break-input-label">Period:</label>
                  <div className="break-input-with-dropdown">
                    <input
                      type="text"
                      value={newBreak.period}
                      onChange={(e) => setNewBreak(prev => ({ ...prev, period: e.target.value }))}
                      disabled={mode === 'view'}
                      className="break-input"
                      placeholder="P01"
                    />
                    <button className="break-dropdown-btn">
                      ▼
                    </button>
                  </div>
                </div>
                <div className="break-input-group">
                  <label className="break-input-label">Break Description:</label>
                  <input
                    type="text"
                    value={newBreak.description}
                    onChange={(e) => setNewBreak(prev => ({ ...prev, description: e.target.value }))}
                    disabled={mode === 'view'}
                    className="break-input"
                    placeholder="Assembly"
                  />
                </div>
                <div className="break-input-group">
                  <button
                    onClick={() => addBreak(true)}
                    disabled={mode === 'view' || !newBreak.period}
                    className="update-button"
                  >
                    Update
                  </button>
                </div>
              </div>
            </div>

            {/* Break Before Periods */}
            <div className="break-before-section">
              <label className="break-before-label">Break Before Periods:</label>
              <input
                type="number"
                value={breakBeforePeriods}
                onChange={(e) => setBreakBeforePeriods(parseInt(e.target.value) || 0)}
                disabled={mode === 'view'}
                className="break-before-input"
              />
            </div>

            {/* Break List */}
            <div className="break-list-section">
              <h4 className="break-list-title">Break List</h4>
              <div className="break-list">
                {specialBreaks.map((breakItem, index) => (
                  <div key={index} className="break-item">
                    <span className="break-item-text">
                      {breakItem.period}-{breakItem.description || 'Empty'}
                    </span>
                    {mode === 'modify' && (
                      <button
                        onClick={() => removeBreak(index, true)}
                        className="remove-break-btn"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="instructions-section">
        <h4 className="instructions-title">時間表設定指引</h4>
        <div className="instructions-content">
          <p><strong>1. 按「編修」鍵進行編修：</strong> 點擊「編修」按鈕開始編輯時間表設定。</p>
          <p><strong>2. 輸入學年開始及結束日期：</strong> 如不使用夏令時間表，在冬季時間欄位鍵入相同的學年開始及結束日期。</p>
          <p><strong>3. 鍵入課節的開始及結束時間：</strong> 如有使用夏令時間表或特別日者，請同時鍵入有關的課節時間。</p>
          <p><strong>4. 鍵入休堂時段資料：</strong> 如小息、午膳等。如用者所屬學校於首節課前有集會時間，亦可於此鍵入有關資料。</p>
          <p><strong>5. 冬季時間表和夏令時間表的結構必須相同：</strong> 如冬季時間表的小息在第三節課前，則夏令時間表的小息亦是設在相同時段。</p>
          <p><strong>6. 輸入完成後按「儲存」鍵紀錄資料：</strong> 按「前頁」鍵回首頁畫面，再按「關閉」鍵回主畫面。</p>
        </div>
      </div>
    </div>
  );
};

export default TimetableSettings;
